/**
 * Module-singleton clock that tracks the server's time relative to this
 * device.
 *
 * The device's absolute clock is never trusted directly — only the elapsed
 * time between two local `Date.now()` readings is used. That is the whole
 * reason this exists: a device clock that is hours wrong, or that jumps
 * while the page is open (a laptop waking from sleep, a user changing their
 * system time), cancels out, because we only ever measure *how much local
 * time has passed since the anchor*, never what the device thinks the
 * absolute time is. Same maths as `useLiveQuizRoom`'s per-payload anchor,
 * extracted here so any countdown on the page — not just the live quiz room
 * itself — can share one clock without its own transport.
 */

interface ClockAnchor {
  serverMs: number;
  localMs: number;
}

let currentAnchor: ClockAnchor | null = null;

/**
 * Records the server's time against the local clock at this instant.
 * Call this whenever a response carries a server timestamp (e.g. a
 * `serverNow` field). An unparseable input is ignored rather than poisoning
 * an otherwise-good anchor.
 */
export function anchor(serverIso: string): void {
  const serverMs = Date.parse(serverIso);
  if (Number.isNaN(serverMs)) {
    return;
  }
  currentAnchor = { serverMs, localMs: Date.now() };
}

/**
 * The server's current time, corrected for however long has elapsed locally
 * since the last anchor. Falls back to the device clock when unanchored —
 * better than throwing, and no worse than doing nothing.
 */
export function serverNow(): number {
  if (!currentAnchor) {
    return Date.now();
  }
  return currentAnchor.serverMs + (Date.now() - currentAnchor.localMs);
}

/** Whether a server timestamp has been recorded yet. */
export function isAnchored(): boolean {
  return currentAnchor !== null;
}

/** Test-only escape hatch to reset the singleton between test cases. */
export function __resetForTests(): void {
  currentAnchor = null;
}
